import { useState } from "react";
import { Calendar, Lock, Unlock, RotateCcw } from "lucide-react";

const EffectiveDateBadge = ({
  date,
  isRTL = false,
  mode = "locked",
  theme = "light",
  onUnlock,
  onReset,
  onDateChange,
}) => {
  // All hooks must be at the top level
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [unlockHover, setUnlockHover] = useState(false);
  const [resetHover, setResetHover] = useState(false);

  const isDark = theme === "dark";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // ─── Theme Colors ──────────────────────────────────────────────────────────
  const primary = {
    base: "#2e7cba",
    light: "#5da3da",
    dark: "#1a5b8e",
  };

  const colors = {
    locked: {
      bg: isDark ? "rgba(51,65,85,0.35)" : "rgba(248,250,252,0.9)",
      bgHover: isDark ? "rgba(51,65,85,0.5)" : "rgba(241,245,249,1)",
      text: isDark ? "rgba(148,163,184,0.9)" : "#64748b",
      border: isDark ? "rgba(148,163,184,0.15)" : "rgba(226,232,240,0.8)",
      icon: isDark ? "#94a3b8" : "#64748b",
    },
    synced: {
      bg: isDark ? "rgba(46,124,186,0.12)" : "rgba(46,124,186,0.08)",
      bgHover: isDark ? "rgba(46,124,186,0.2)" : "rgba(46,124,186,0.12)",
      text: primary.base,
      textLight: isDark ? primary.light : primary.dark,
      border: isDark ? "rgba(93,163,218,0.25)" : "rgba(46,124,186,0.2)",
      icon: primary.light,
      glow: isDark
        ? "0 0 0 1px rgba(46,124,186,0.2), 0 4px 12px rgba(46,124,186,0.15)"
        : "0 0 0 1px rgba(46,124,186,0.1), 0 2px 8px rgba(46,124,186,0.1)",
    },
    custom: {
      bg: isDark ? "rgba(245,158,11,0.12)" : "rgba(245,158,11,0.08)",
      bgHover: isDark ? "rgba(245,158,11,0.2)" : "rgba(245,158,11,0.12)",
      text: "#f59e0b",
      textLight: isDark ? "#fbbf24" : "#d97706",
      border: isDark ? "rgba(251,191,36,0.25)" : "rgba(245,158,11,0.2)",
      icon: "#fbbf24",
      glow: isDark
        ? "0 0 0 1px rgba(245,158,11,0.2), 0 4px 12px rgba(245,158,11,0.15)"
        : "0 0 0 1px rgba(245,158,11,0.1), 0 2px 8px rgba(245,158,11,0.1)",
    },
  };

  const currentColors = colors[mode];

  const baseStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 14px",
    borderRadius: "12px",
    fontSize: "13px",
    fontWeight: "600",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: mode !== "locked" ? "default" : "default",
    userSelect: "none",
  };

  // ─── LOCKED MODE (Read-only) ──────────────────────────────────────────────
  if (mode === "locked") {
    return (
      <div
        style={{
          ...baseStyle,
          background: isHovered ? currentColors.bgHover : currentColors.bg,
          color: currentColors.text,
          border: `1px solid ${currentColors.border}`,
          boxShadow: isDark
            ? "0 2px 6px rgba(0,0,0,0.2)"
            : "0 1px 3px rgba(0,0,0,0.03)",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "6px",
            background: isDark
              ? "rgba(148,163,184,0.15)"
              : "rgba(100,116,139,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Lock size={12} style={{ color: currentColors.icon }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span
            style={{
              fontSize: "10px",
              opacity: 0.7,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            {isRTL ? "ساري من" : "Effective"}
          </span>
          <span style={{ fontWeight: "700", fontSize: "13px" }}>
            {formatDate(date)}
          </span>
        </div>
      </div>
    );
  }

  // ─── SYNCED MODE (Following global date) ──────────────────────────────────
  if (mode === "synced") {
    return (
      <div
        style={{
          ...baseStyle,
          background: isHovered ? currentColors.bgHover : currentColors.bg,
          color: currentColors.text,
          border: `1px solid ${currentColors.border}`,
          boxShadow: currentColors.glow,
          position: "relative",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Animated pulse indicator */}
        <div
          style={{
            position: "absolute",
            top: "-2px",
            [isRTL ? "right" : "left"]: "-2px",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: currentColors.icon,
            boxShadow: `0 0 0 ${isHovered ? "4px" : "0px"} ${isDark ? "rgba(93,163,218,0.2)" : "rgba(46,124,186,0.15)"}`,
            transition: "box-shadow 0.3s ease",
          }}
        />

        <div
          style={{
            width: "24px",
            height: "24px",
            borderRadius: "8px",
            background: isDark
              ? "rgba(46,124,186,0.2)"
              : "rgba(46,124,186,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Calendar size={14} style={{ color: currentColors.icon }} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span
              style={{
                fontSize: "10px",
                opacity: 0.7,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              }}
            >
              {isRTL ? "ساري من" : "Effective"}
            </span>
            <span
              style={{
                fontSize: "9px",
                padding: "1px 5px",
                borderRadius: "4px",
                background: isDark
                  ? "rgba(93,163,218,0.15)"
                  : "rgba(46,124,186,0.1)",
                color: currentColors.textLight,
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {isRTL ? "متزامن" : "Synced"}
            </span>
          </div>
          <span style={{ fontWeight: "700", fontSize: "13px" }}>
            {formatDate(date)}
          </span>
        </div>

        {onUnlock && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUnlock();
            }}
            onMouseEnter={() => setUnlockHover(true)}
            onMouseLeave={() => setUnlockHover(false)}
            style={{
              marginLeft: isRTL ? "0" : "8px",
              marginRight: isRTL ? "8px" : "0",
              padding: "6px",
              background: unlockHover
                ? isDark
                  ? "rgba(46,124,186,0.25)"
                  : "rgba(46,124,186,0.15)"
                : "transparent",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: unlockHover ? "scale(1.1)" : "scale(1)",
            }}
            title={isRTL ? "فك الربط لتعيين تاريخ مخصص" : "Unlock to set custom date"}
          >
            <Unlock
              size={15}
              style={{
                color: unlockHover ? currentColors.textLight : currentColors.text,
                transition: "color 0.2s ease",
              }}
            />
          </button>
        )}
      </div>
    );
  }

  // ─── CUSTOM MODE (Independent date) ───────────────────────────────────────
  if (mode === "custom") {
    return (
      <div
        style={{
          ...baseStyle,
          background: isHovered ? currentColors.bgHover : currentColors.bg,
          color: currentColors.text,
          border: `1px solid ${currentColors.border}`,
          boxShadow: currentColors.glow,
          position: "relative",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Custom indicator badge */}
        <div
          style={{
            position: "absolute",
            top: "-2px",
            [isRTL ? "right" : "left"]: "-2px",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: currentColors.icon,
            boxShadow: `0 0 0 ${isHovered ? "4px" : "0px"} ${isDark ? "rgba(251,191,36,0.2)" : "rgba(245,158,11,0.15)"}`,
            transition: "box-shadow 0.3s ease",
          }}
        />

        <div
          style={{
            width: "24px",
            height: "24px",
            borderRadius: "8px",
            background: isDark
              ? "rgba(245,158,11,0.2)"
              : "rgba(245,158,11,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Calendar size={14} style={{ color: currentColors.icon }} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span
              style={{
                fontSize: "10px",
                opacity: 0.7,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              }}
            >
              {isRTL ? "ساري من" : "Effective"}
            </span>
            <span
              style={{
                fontSize: "9px",
                padding: "1px 5px",
                borderRadius: "4px",
                background: isDark
                  ? "rgba(251,191,36,0.15)"
                  : "rgba(245,158,11,0.1)",
                color: currentColors.textLight,
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {isRTL ? "مخصص" : "Custom"}
            </span>
          </div>

          {onDateChange ? (
            <div style={{ position: "relative" }}>
              <input
                type="date"
                value={date || new Date().toISOString().split("T")[0]}
                onChange={(e) => onDateChange(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                style={{
                  padding: "6px 10px",
                  fontSize: "13px",
                  fontWeight: "700",
                  borderRadius: "8px",
                  border: isFocused
                    ? `2px solid ${currentColors.text}`
                    : `1px solid ${currentColors.border}`,
                  background: isDark
                    ? "rgba(30,41,59,0.6)"
                    : "#ffffff",
                  color: currentColors.text,
                  outline: "none",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: isFocused
                    ? currentColors.glow
                    : isDark
                    ? "0 1px 3px rgba(0,0,0,0.2)"
                    : "0 1px 2px rgba(0,0,0,0.03)",
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ) : (
            <span style={{ fontWeight: "700", fontSize: "13px" }}>
              {formatDate(date)}
            </span>
          )}
        </div>

        {onReset && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReset();
            }}
            onMouseEnter={() => setResetHover(true)}
            onMouseLeave={() => setResetHover(false)}
            style={{
              marginLeft: isRTL ? "0" : "8px",
              marginRight: isRTL ? "8px" : "0",
              padding: "6px",
              background: resetHover
                ? isDark
                  ? "rgba(245,158,11,0.25)"
                  : "rgba(245,158,11,0.15)"
                : "transparent",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: resetHover ? "scale(1.1)" : "scale(1)",
            }}
            title={isRTL ? "إعادة المزامنة مع التاريخ العام" : "Re-sync with global date"}
          >
            <RotateCcw
              size={15}
              style={{
                color: resetHover ? currentColors.textLight : currentColors.text,
                transition: "color 0.2s ease",
              }}
            />
          </button>
        )}
      </div>
    );
  }

  return null;
};

export default EffectiveDateBadge;
